// File: src/components/EventQuestionEditor.js
// Extras an organizer asks about on their own event (lisäpalvelut).
//
// Some events need to know things the entry form has no field for: does this
// competitor want lunch and how many portions, how many sets of tyres to put
// aside, do they need pit space. This is where the organizer writes those
// questions; the entrant answers them in the entry form, and the entrant list
// adds them up.
//
// Quantities only. There is no price column anywhere in this feature - a
// pre-order here is a number the organizer collects, and the money is settled
// however they already settle it.
//
// The question text is the organizer's own words in their own language, stored
// as typed and never translated. Only the scaffolding around it is.

import React, { useState } from 'react';
import { Plus, Trash2, Edit, Check, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { apiPost, apiPut, apiDelete } from '../services/api';

// Mirrors the enum on event_questions.answer_type and QUESTION_TYPES in
// backend/events.js. The label key is looked up at render time so the list can
// stay a plain constant.
const ANSWER_TYPES = [
  { value: 'yes_no', labelKey: 'extraTypeYesNo' },
  { value: 'number', labelKey: 'extraTypeNumber' },
  { value: 'text', labelKey: 'extraTypeText' }
];

const EMPTY_FORM = {
  label: '', help_text: '', answer_type: 'yes_no',
  required: false, min_value: '', max_value: '', depends_on_question_id: ''
};

// The server explains refusals (answers already given) in `message`; `error` is
// only the short code.
const describe = (err, fallback) => (err.body && err.body.message) || err.message || fallback;

const toPayload = (form) => ({
  label: form.label.trim(),
  help_text: form.help_text.trim() || null,
  answer_type: form.answer_type,
  required: Boolean(form.required),
  // Bounds are a count's business only. The server clears them for the other
  // two types anyway; not sending them keeps the two ends saying the same thing.
  min_value: form.answer_type === 'number' && form.min_value !== ''
    ? parseInt(form.min_value, 10) : null,
  max_value: form.answer_type === 'number' && form.max_value !== ''
    ? parseInt(form.max_value, 10) : null,
  depends_on_question_id: form.depends_on_question_id
    ? parseInt(form.depends_on_question_id, 10) : null
});

const toForm = (question) => ({
  label: question.label,
  help_text: question.help_text || '',
  answer_type: question.answer_type,
  required: Boolean(question.required),
  min_value: question.min_value === null || question.min_value === undefined
    ? '' : String(question.min_value),
  max_value: question.max_value === null || question.max_value === undefined
    ? '' : String(question.max_value),
  depends_on_question_id: question.depends_on_question_id
    ? String(question.depends_on_question_id) : ''
});

function EventQuestionEditor({ event, token, onChange }) {
  const { t } = useLanguage();
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const questions = event.questions || [];

  // Only a yes/no can be a trigger, and only one that is not itself conditional
  // - the same one-level rule the server enforces. A question is also never
  // offered itself.
  const triggersFor = (questionId) => questions.filter(q =>
    q.answer_type === 'yes_no'
    && !q.depends_on_question_id
    && q.id !== questionId);

  // A question others hang off cannot stop being a yes/no or become conditional
  // itself. The server refuses both; this stops offering them.
  const hasDependents = (questionId) =>
    questions.some(q => q.depends_on_question_id === questionId);

  const triggerLabel = (question) => {
    const trigger = questions.find(q => q.id === question.depends_on_question_id);
    return trigger ? trigger.label : '';
  };

  const typeLabel = (value) => {
    const type = ANSWER_TYPES.find(a => a.value === value);
    return type ? t(type.labelKey) : value;
  };

  const addQuestion = async () => {
    if (!form.label.trim()) {
      setError(t('extraLabelRequired'));
      return;
    }

    setSaving(true);
    setError('');
    try {
      await apiPost(`/events/${event.id}/questions`, toPayload(form), { token });
      setForm(EMPTY_FORM);
      onChange();
    } catch (err) {
      setError(describe(err, t('failedToSaveExtra')));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (question) => {
    setEditingId(question.id);
    setEditForm(toForm(question));
    setError('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(EMPTY_FORM);
    setError('');
  };

  const saveEdit = async (question) => {
    if (!editForm.label.trim()) {
      setError(t('extraLabelRequired'));
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = toPayload(editForm);

      // Once people have answered, the answer type is frozen - the stored
      // numbers would be reinterpreted under a new one. The server refuses it,
      // and sending the unchanged value would trip that guard for no reason.
      if (question.has_answers) delete payload.answer_type;
      // Same reasoning from the other direction: a question others hang off
      // must stay a yes/no, and resending it unchanged would trip that guard.
      if (hasDependents(question.id)) {
        delete payload.answer_type;
        delete payload.depends_on_question_id;
      }

      await apiPut(`/event-questions/${question.id}`, payload, { token });
      cancelEdit();
      onChange();
    } catch (err) {
      setError(describe(err, t('failedToSaveExtra')));
    } finally {
      setSaving(false);
    }
  };

  // Deleting takes the answers with it, so the server refuses while there are
  // any and says how many. That refusal is the confirmation prompt: the
  // organizer is told the cost before being asked a second time, rather than
  // guessing at it from a generic "are you sure".
  const removeQuestion = async (question) => {
    if (!window.confirm(t('deleteExtraConfirm'))) return;

    setError('');
    try {
      await apiDelete(`/event-questions/${question.id}`, { token });
      onChange();
      return;
    } catch (err) {
      if (!err.body || err.body.error !== 'questionHasAnswers') {
        setError(describe(err, t('failedToDeleteExtra')));
        return;
      }

      const confirmed = window.confirm(
        t('deleteExtraDiscardConfirm').replace('{count}', err.body.answers));
      if (!confirmed) return;
    }

    try {
      await apiDelete(`/event-questions/${question.id}?discard_answers=1`, { token });
      onChange();
    } catch (err) {
      setError(describe(err, t('failedToDeleteExtra')));
    }
  };

  const textInput = (value, onSet, placeholder, { background = 'bg-paper-alt' } = {}) => (
    <input
      type="text"
      value={value}
      onChange={(e) => onSet(e.target.value)}
      placeholder={placeholder}
      className={`w-full px-4 py-2 ${background} border border-line rounded text-ink`}
    />
  );

  // The bounds only exist for a count, so they appear with it rather than
  // sitting there greyed out for a yes/no.
  const typeControls = (draft, setter, {
    locked = false, background = 'bg-paper-alt', triggers = [], lockedAsTrigger = false
  } = {}) => (
    <div className="flex flex-wrap items-center gap-2">
      <select
        value={draft.answer_type}
        onChange={(e) => setter({ ...draft, answer_type: e.target.value })}
        disabled={locked}
        className={`px-4 py-2 ${background} border border-line rounded text-ink disabled:opacity-60`}
      >
        {ANSWER_TYPES.map(type => (
          <option key={type.value} value={type.value}>{t(type.labelKey)}</option>
        ))}
      </select>

      {draft.answer_type === 'number' && (
        <>
          <input
            type="number"
            value={draft.min_value}
            onChange={(e) => setter({ ...draft, min_value: e.target.value })}
            placeholder={t('extraMinValue')}
            className={`w-24 px-3 py-2 ${background} border border-line rounded text-ink`}
          />
          <input
            type="number"
            value={draft.max_value}
            onChange={(e) => setter({ ...draft, max_value: e.target.value })}
            placeholder={t('extraMaxValue')}
            className={`w-24 px-3 py-2 ${background} border border-line rounded text-ink`}
          />
        </>
      )}

      <label className="flex items-center gap-2 cursor-pointer text-ink-muted">
        <input
          type="checkbox"
          checked={draft.required}
          onChange={(e) => setter({ ...draft, required: e.target.checked })}
          className="w-4 h-4"
        />
        {t('extraRequired')}
      </label>

      {/* Only offered when there is a yes/no to hang off, so an event with one
          question does not get a select with nothing in it. */}
      {triggers.length > 0 && !lockedAsTrigger && (
        <select
          value={draft.depends_on_question_id}
          onChange={(e) => setter({ ...draft, depends_on_question_id: e.target.value })}
          className={`px-4 py-2 ${background} border border-line rounded text-ink`}
        >
          <option value="">{t('extraAlwaysAsked')}</option>
          {triggers.map(q => (
            <option key={q.id} value={q.id}>
              {t('extraOnlyWhen').replace('{question}', q.label)}
            </option>
          ))}
        </select>
      )}
    </div>
  );

  return (
    <div>
      <label className="block text-ink-muted mb-2 font-semibold">{t('eventExtras')}</label>
      <p className="text-ink-muted text-sm mb-3">{t('eventExtrasHelp')}</p>

      {questions.length === 0 ? (
        <p className="text-ink-muted text-sm mb-3">{t('eventNoExtras')}</p>
      ) : (
        <div className="space-y-2 mb-3">
          {questions.map(question => (
            editingId === question.id ? (
              <div key={question.id} className="bg-paper-alt px-3 py-3 rounded space-y-2">
                {textInput(editForm.label,
                  (v) => setEditForm({ ...editForm, label: v }), t('extraLabel'),
                  { background: 'bg-paper' })}
                {textInput(editForm.help_text,
                  (v) => setEditForm({ ...editForm, help_text: v }), t('extraHelpText'),
                  { background: 'bg-paper' })}
                {typeControls(editForm, setEditForm, {
                  locked: Boolean(question.has_answers) || hasDependents(question.id),
                  background: 'bg-paper',
                  triggers: triggersFor(question.id),
                  lockedAsTrigger: hasDependents(question.id)
                })}

                {/* Everything else stays editable while entry is open - the
                    wording, the help text, the bounds. Only the type is frozen,
                    and only because the answers already given would change
                    meaning under a new one. */}
                {Boolean(question.has_answers) && (
                  <p className="text-ink-muted text-sm">{t('extraTypeLocked')}</p>
                )}

                {/* Locked from the other end: this one is somebody's trigger. */}
                {hasDependents(question.id) && (
                  <p className="text-ink-muted text-sm">{t('extraDependencyLocked')}</p>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={() => saveEdit(question)}
                    disabled={saving}
                    className="bg-accent hover:opacity-90 disabled:opacity-50 text-accent-ink px-4 py-2 rounded font-semibold flex items-center gap-2"
                  >
                    <Check className="w-4 h-4" />
                    {t('save')}
                  </button>
                  <button
                    onClick={cancelEdit}
                    className="bg-paper hover:opacity-80 text-ink px-4 py-2 rounded font-semibold flex items-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    {t('cancel')}
                  </button>
                </div>
              </div>
            ) : (
              <div
                key={question.id}
                className={`flex items-center gap-3 bg-paper-alt px-3 py-2 rounded${
                  question.depends_on_question_id ? ' ml-6' : ''}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-ink font-medium truncate">
                    {question.label}
                    {question.required ? ' *' : ''}
                  </div>
                  <div className="text-ink-muted text-sm truncate">
                    {typeLabel(question.answer_type)}
                    {question.answer_type === 'number' && question.min_value !== null
                      ? ` • ${t('extraMinValue')} ${question.min_value}` : ''}
                    {question.answer_type === 'number' && question.max_value !== null
                      ? ` • ${t('extraMaxValue')} ${question.max_value}` : ''}
                    {question.depends_on_question_id
                      ? ` • ${t('extraOnlyWhen').replace('{question}', triggerLabel(question))}`
                      : ''}
                    {question.help_text ? ` • ${question.help_text}` : ''}
                  </div>
                </div>

                <button
                  onClick={() => startEdit(question)}
                  className="text-blue-700 dark:text-blue-400 hover:opacity-80"
                  title={t('edit')}
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button
                  onClick={() => removeQuestion(question)}
                  className="text-red-700 dark:text-red-400 hover:opacity-80"
                  title={t('delete')}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )
          ))}
        </div>
      )}

      {/* Adding is hidden while editing, so there is only ever one form open. */}
      {editingId === null && (
        <div className="space-y-2">
          {textInput(form.label, (v) => setForm({ ...form, label: v }), t('extraLabel'))}
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2">
            {textInput(form.help_text,
              (v) => setForm({ ...form, help_text: v }), t('extraHelpText'))}
            <button
              onClick={addQuestion}
              disabled={saving}
              className="bg-paper-alt hover:opacity-80 disabled:opacity-50 text-ink px-4 py-2 rounded font-semibold flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              {t('eventAddExtra')}
            </button>
          </div>
          {typeControls(form, setForm, { triggers: triggersFor(null) })}
        </div>
      )}

      {error && (
        <div className="mt-3 bg-red-50 dark:bg-red-500/20 border border-red-500 text-red-800 dark:text-red-200 px-4 py-3 rounded text-sm">
          {error}
        </div>
      )}
    </div>
  );
}

export default EventQuestionEditor;
